import {
  Box,
  Button,
  Container,
  Divider,
  LinearProgress,
  List,
  ListItem,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { PageTitleBar } from "common/components";
import { useTranslation } from "common/locales";
import { useEffect, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import styled from "styled-components";

import { mixpanel } from "common/analytics";
import FileTabs, { FileTabValue } from "common/components/FileTabs";
import {
  AccessibleUser,
  FileEntry,
  getAllFileEntriesForUser,
  selectAllAccessibleUsers,
  selectAllFileEntries,
  useAppDispatch,
  useAppSelector,
} from "model";
import ClosableList from "./ClosableList";
import FileEntryListItem from "./FileEntryListItem";
import ListOfUsersWithFileAccess from "./ListOfUsersWithFileAccess";

export default function FileEntryDetail() {
  const history = useHistory();
  const { t } = useTranslation();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [activeTab, setActiveTab] = useState<FileTabValue>("all");
  const fileEntriesLoading = useAppSelector(
    (state) => state.fileEntries.loading
  );

  const allFileEntries: Array<FileEntry> = useAppSelector((state) =>
    selectAllFileEntries(state)
  );

  const allAccessibleUsers: Array<AccessibleUser> = useAppSelector((state) =>
    selectAllAccessibleUsers(state)
  );

  const allAccessibleUsersMap: { [guid: string]: AccessibleUser } = {};
  allAccessibleUsers.forEach((user) => {
    allAccessibleUsersMap[user.guid] = user;
  });

  const sortedFileEntries = allFileEntries.sort(
    (a: FileEntry, b: FileEntry) =>
      new Date(b.creationDate ?? "").valueOf() -
      new Date(a.creationDate ?? "").valueOf()
  );
  const user = useAppSelector((state) => state.user.details);

  // Check if navigation came from push notification and refresh data
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    if (searchParams.get("fromPushNotification") === "true") {
      // Refresh file entries data
      void dispatch(getAllFileEntriesForUser({ associatedUserId: user?.guid }));

      // Clear the query parameter to avoid repeated refreshes
      searchParams.delete("fromPushNotification");
      const newSearch = searchParams.toString();
      const newUrl = `${location.pathname}${newSearch ? `?${newSearch}` : ""}`;
      history.replace(newUrl);
    }
  }, [location.search, dispatch, user?.guid, history, location.pathname]);

  // Filter files based on active tab
  const filteredFileEntries = sortedFileEntries.filter((fileEntry) => {
    switch (activeTab) {
      case "uploaded-by-me":
        return fileEntry.associatedUserId === user?.guid;
      case "uploaded-by-others":
        return fileEntry.associatedUserId !== user?.guid;
      case "all":
      default:
        return true;
    }
  });

  // Files where associatedUserId is the current user (My files)
  const myFileEntries = filteredFileEntries.filter(
    (fileEntry) => fileEntry.associatedUserId === user?.guid
  );

  // Files where associatedUserId is NOT the current user (Someone else's files)
  const otherUserFileEntries = filteredFileEntries.filter(
    (fileEntry) => fileEntry.associatedUserId !== user?.guid
  );

  type UserFileListMap = {
    [name: string]: Array<FileEntry>;
  };

  // Bucket other users' files by associatedUserId for collapsed lists
  const bucketedOtherUserFileEntries: UserFileListMap = {};
  otherUserFileEntries.forEach((fileEntry) => {
    const associatedUserId = fileEntry.associatedUserId;
    if (!bucketedOtherUserFileEntries[associatedUserId]) {
      bucketedOtherUserFileEntries[associatedUserId] = [];
    }
    bucketedOtherUserFileEntries[associatedUserId].push(fileEntry);
  });

  if (fileEntriesLoading !== "idle") {
    return <LinearProgress />;
  }

  console.log("Rendering FileEntryDetail");
  console.log(`isMobile: ${isMobile}  `);

  return (
    <>
      <PageTitleBar title={t("go.to.file.management.button")} showDivider />
      <Container
        maxWidth="sm"
        sx={{
          pt: isMobile ? 2 : 0,
          paddingTop: isMobile
            ? "calc(env(safe-area-inset-top) + 16px)"
            : undefined,
        }}
      >
        <List>
          <ListItem key="add-file">
            <Button
              variant="contained"
              color="primary"
              size="large"
              fullWidth
              onClick={() => {
                mixpanel.track("Add new file clicked");
                history.push(`/add-new-file`);
              }}
            >
              {t("fileManagementDetailView.button.addFile.title")}
            </Button>
          </ListItem>
        </List>

        {/* Tabs for filtering files */}
        <Box sx={{ display: "flex", justifyContent: "center", mt: 2, mb: 2 }}>
          <FileTabs value={activeTab} onChange={setActiveTab} />
        </Box>
      </Container>

      <FileEntryListContainer
        maxWidth="sm"
        sx={{
          pb: isMobile ? 10 : 2, // Add bottom padding for mobile bottom navigation
        }}
      >
        <ListOfUsersWithFileAccess></ListOfUsersWithFileAccess>
      </FileEntryListContainer>

      <Divider />

      {/* Render based on active tab */}
      {activeTab === "all" && (
        <FileEntryListContainer maxWidth="sm">
          <List>
            {filteredFileEntries.map((fileEntry) => (
              <FileEntryListItem key={fileEntry.guid} fileEntry={fileEntry} />
            ))}
          </List>
        </FileEntryListContainer>
      )}

      {activeTab === "uploaded-by-me" && (
        <FileEntryListContainer maxWidth="sm">
          <List>
            {myFileEntries.map((fileEntry) => (
              <FileEntryListItem key={fileEntry.guid} fileEntry={fileEntry} />
            ))}
          </List>
        </FileEntryListContainer>
      )}

      {activeTab === "uploaded-by-others" && (
        <>
          {Object.entries(bucketedOtherUserFileEntries).map(
            ([associatedUserId, fileEntriesForUser]) => {
              const accessibleUser: AccessibleUser | undefined =
                allAccessibleUsersMap[associatedUserId];
              return (
                <>
                  <FileEntryListContainer key={associatedUserId} maxWidth="sm">
                    <ClosableList
                      fileEntries={fileEntriesForUser}
                      title={t("file.uploaded.for", {
                        receiverName:
                          accessibleUser?.name ?? accessibleUser?.mail,
                      })}
                    />
                  </FileEntryListContainer>
                  <Divider />
                </>
              );
            }
          )}
        </>
      )}
    </>
  );
}

const FileEntryListContainer = styled(Container)``;
